import { flagText } from '@tabel/shared/rules-answer';
import type { MySample } from '@tabel/shared/schemas/agent-data';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ERROR_TEXT } from '../../components/EngineDay';
import { api, rulesKeys } from '../../lib/api';
import { ruDate } from '../../lib/format';
import { useRole } from '../../lib/role';
import { CONDITION_LABEL, STATE_WORD, say } from './texts';

/*
 * Условие выборки словами (FR-023, FR-026) — для журнала выборок администратора системы и «Моих выборок»
 * (`pages/MyAgents`). Сервер присылает условие, как его прислал агент (`condition` — коды и числа, вход `days_sample`),
 * слова пишет интерфейс:
 * - правило — его вопросом со страницы «Правила расчёта» (`GET /api/rules`, `groups[].question`), а не номером;
 *   правила нет в правилах на сегодня — так и написано; правила ещё не загрузились — только «Правило»;
 * - версия правил — номером версии из истории правил (`GET /api/rules/history`, `id → version`), а не номером записи;
 * - пометка — `flagText` (как на странице правил), состояние дня — словами итога дня, ошибка расчёта — словами
 *   карточки дня (`EngineDay`); кода без слов — подпись и код отдельно;
 * - даты — «дд.мм.гггг», часы — «от … до … ч». «Передать день агенту» — один день без условия.
 * Правила и историю страница запрашивает, только если в условиях есть правило или версия (`useConditionNames`).
 */

/** Выборка для условия: источник и условие (`MySample`, `SampleLogEntry`). */
type Sample = Pick<MySample, 'source' | 'condition'>;

/** Названия из «Правил расчёта». */
export interface ConditionNames {
  /** Вопрос правила по номеру; `null` — правила нет в правилах на сегодня; `undefined` — правила не загружены. */
  rule(group: string): string | null | undefined;
  /** Номер версии правил по номеру записи версии; `undefined` — история не загружена или записи нет. */
  version(id: number): number | undefined;
}

/** Названий нет: правило — «Правило», версия — «Версия правил». */
export const NO_NAMES: ConditionNames = { rule: () => undefined, version: () => undefined };

const textOf = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() !== '' ? v : undefined);
const numberOf = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;

/** Части условия: «Правило «…»», «Даты: с … по …». Пустое условие — «Без условия». */
export function conditionParts(s: Sample, names: ConditionNames = NO_NAMES): string[] {
  if (s.source === 'day_link') return [CONDITION_LABEL.dayLink];
  const c = s.condition;
  const parts: string[] = [];
  const group = textOf(c.group);
  if (group !== undefined) {
    const question = names.rule(group);
    parts.push(
      question ? say.rule(question) : question === null ? CONDITION_LABEL.ruleGone : CONDITION_LABEL.group,
    );
  }
  const flag = textOf(c.flag);
  if (flag !== undefined) parts.push(say.flag(flagText(flag)));
  const state = textOf(c.state);
  if (state !== undefined) parts.push(`${CONDITION_LABEL.state}: ${STATE_WORD[state] ?? flagText(state)}`);
  const error = textOf(c.errorCode);
  if (error !== undefined) {
    const words = ERROR_TEXT[error];
    parts.push(words ? `${CONDITION_LABEL.errorCode}: ${words}` : say.errorCode(error));
  }
  const versionId = numberOf(c.versionId);
  if (versionId !== undefined) {
    const n = names.version(versionId);
    parts.push(n === undefined ? CONDITION_LABEL.versionId : say.version(n));
  }
  const from = textOf(c.from);
  const to = textOf(c.to);
  if (from || to)
    parts.push(`${CONDITION_LABEL.dates}: ${say.dates(from ? ruDate(from) : null, to ? ruDate(to) : null)}`);
  const min = numberOf(c.hoursMin);
  const max = numberOf(c.hoursMax);
  if (min !== undefined || max !== undefined)
    parts.push(`${CONDITION_LABEL.hours}: ${say.hours(min ?? null, max ?? null)}`);
  if (c.changedByDraft === true) parts.push(CONDITION_LABEL.changedByDraft);
  return parts.length > 0 ? parts : [CONDITION_LABEL.none];
}

/** Условие одной строкой: части через «; ». */
export function describeCondition(s: Sample, names: ConditionNames = NO_NAMES): string {
  return conditionParts(s, names).join('; ');
}

/** Правила и история меняются редко: 5 минут без повторного запроса. */
const NAMES_STALE_MS = 5 * 60_000;

/**
 * Названия для условий выборок: правила на сегодня и история правил — те же запросы и ключи кэша, что у страницы
 * «Правила расчёта» (`rulesKeys`). Запрос идёт, только если правило или версия есть хотя бы в одном условии. Отказ или
 * сбой — названий нет (`NO_NAMES` для этой части): условие всё равно видно.
 */
export function useConditionNames(samples: readonly Sample[] | undefined): ConditionNames {
  const { role } = useRole();
  const list = samples ?? [];
  const byCondition = list.filter((s) => s.source === 'condition');
  const needRules = byCondition.some((s) => textOf(s.condition.group) !== undefined);
  const needVersions = byCondition.some((s) => numberOf(s.condition.versionId) !== undefined);
  const rules = useQuery({
    queryKey: rulesKeys.view(role, {}),
    queryFn: () => api.rules(),
    enabled: needRules,
    staleTime: NAMES_STALE_MS,
  });
  const versions = useQuery({
    queryKey: rulesKeys.history(role),
    queryFn: () => api.rulesHistory(),
    enabled: needVersions,
    staleTime: NAMES_STALE_MS,
  });
  const groups = rules.data?.groups;
  const history = versions.data;
  return useMemo(() => {
    const questions = Array.isArray(groups) ? new Map(groups.map((g) => [g.id, g.question] as const)) : null;
    const numbers = Array.isArray(history) ? new Map(history.map((v) => [v.id, v.version] as const)) : null;
    return {
      rule: (group) => (questions ? (questions.get(group) ?? null) : undefined),
      version: (id) => numbers?.get(id),
    };
  }, [groups, history]);
}
